import { groupBy } from "lodash";
import { ChangeEvent, FC, ReactNode, useMemo } from "react";
import { useHistory } from "react-router";

import { IPickerListItem, Info, Input, Picker, SpacingToken, Tooltip } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { config } from "config/config";
import { alphabeticalSort } from "helpers/sort";
import { IEmailDomain } from "store/entities/email-domains";
import { IMailboxReplyAddress } from "store/entities/mailboxes/interfaces";
import { withVar } from "themes/helpers";

import { FormFieldNoMargin } from "../../FormFieldNoMargin";
import { EmailSettingsRoutes } from "../../routes";

interface IReplyAddressInput extends Omit<IMailboxReplyAddress, "emailDomainID"> {
  emailDomainID: string | null;
}

interface IReplyAddressPickerProps {
  replyAddress: IReplyAddressInput;
  setReplyAddress: (input: IReplyAddressInput) => void;
  domains: Record<string, IEmailDomain>;
  licenseId: number;
  className?: string;
  error?: string;
  description?: ReactNode;
  disabled?: boolean;
  isEditing?: boolean;
  validate: () => void;
}

const NONE_ITEM = { key: "none", name: `@${config.defaultMailboxSuffix}` };
const ADD_NEW_DOMAIN_ITEM = { key: "add-new-domain", name: "Add new domain…" };

const ReplyAddressPicker: FC<IReplyAddressPickerProps> = ({
  domains,
  replyAddress,
  licenseId,
  setReplyAddress,
  validate,
  error,
  disabled = false,
}) => {
  const history = useHistory();
  const { prefix, emailDomainID } = replyAddress;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handlePrefixChange = (event: ChangeEvent<HTMLInputElement>) => {
    setReplyAddress({ prefix: event.target.value, emailDomainID });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleDomainChange = (selected: IPickerListItem[] | null) => {
    if (!selected) {
      return;
    }
    const itemKey = selected[0].key;
    if (itemKey === "add-new-domain") {
      history.push(EmailSettingsRoutes.DomainsWithModal);
    } else {
      setReplyAddress({
        prefix: replyAddress.emailDomainID === null ? "" : replyAddress.prefix,
        emailDomainID: itemKey === "none" ? null : itemKey,
      });
    }
  };

  const selectItems = useMemo(() => {
    const { helpdesk, custom } = groupBy(Object.values(domains), domain =>
      domain.name.endsWith(".helpdesk.com") ? "helpdesk" : "custom"
    );
    const sortedHelpdeskDomains = (helpdesk || []).sort((d, d2) => alphabeticalSort(d.name, d2.name));
    const sortedCustomDomains = (custom || []).sort((d, d2) => alphabeticalSort(d.name, d2.name));

    return [
      ...sortedCustomDomains.map(d => ({ key: d.id, name: `@${d.name}` })),
      ...sortedHelpdeskDomains.map(d => ({ key: d.id, name: `@${d.name}` })),
      NONE_ITEM,
      ADD_NEW_DOMAIN_ITEM,
    ];
  }, [domains]);

  const selectedItem = selectItems.find(item => item.key === emailDomainID);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const picker = () => (
    <Flex flexWrap gap={withVar(SpacingToken.Spacing2)}>
      <Block flex="1" minWidth="100px">
        <Input
          value={emailDomainID ? prefix : licenseId.toString()}
          disabled={!emailDomainID || disabled}
          onChange={handlePrefixChange}
          onBlur={validate}
          id="prefix"
          error={!!error}
        />
      </Block>
      <Block flex="1">
        <Picker
          id="email-domain"
          disabled={disabled}
          onSelect={handleDomainChange}
          options={selectItems}
          selected={[selectedItem || NONE_ITEM]}
          searchDisabled
          hideClearButton
        />
      </Block>
    </Flex>
  );

  return (
    <FormFieldNoMargin labelText="Reply address" error={error}>
      {disabled ? (
        <Tooltip triggerRenderer={picker} placement="top" kind="invert">
          <Info
            theme="invert"
            text="The prefix and domain of the reply address cannot be modified. Add another reply address if you need a new prefix or domain."
          />
        </Tooltip>
      ) : (
        picker()
      )}
    </FormFieldNoMargin>
  );
};

export default ReplyAddressPicker;
